import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type Paginated } from '@/lib/api';
import { Button, Card, EmptyState, ErrorState, Input, Loading, Select, cx } from './ui';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** Right-align numeric columns so they scan vertically. */
  numeric?: boolean;
  className?: string;
}

export interface FilterOption {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
}

interface DataTableProps<T> {
  /** React Query cache key prefix. */
  queryKey: string;
  /** API path, e.g. `/bots`. */
  path: string;
  columns: Array<Column<T>>;
  searchable?: boolean;
  searchPlaceholder?: string;
  filters?: FilterOption[];
  /** Extra query params merged into every request. */
  params?: Record<string, string | number | boolean | undefined>;
  onRowClick?: (row: T) => void;
  /**
   * How many rows there are in total, reported up as it changes.
   *
   * The count already exists in here but was only rendered when there was
   * more than one page of it -- so a page showing every transfer it had
   * showed no number at all, which is exactly when somebody is asking "how
   * many". Lifted so a page can put it in its own header.
   */
  onTotal?: (total: number) => void;
  /**
   * The filters and search currently applied, reported as they change.
   *
   * For an export button that means it. A button outside this component has
   * no idea what is filtered inside it, so one wired up naively hands back
   * everything -- and an operator who has narrowed to one campaign and
   * pressed it believes the file is that campaign. Nothing in a CSV says
   * otherwise.
   *
   * Paging is stripped: a page number is a fact about this screen, not about
   * what somebody asked to export.
   */
  onQuery?: (query: Record<string, string | number | boolean | null | undefined>) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  actions?: ReactNode;
  title?: string;
  description?: string;
  pageSize?: number;
  /** Poll interval in ms — used by the live call board. */
  refetchInterval?: number;
}

export function DataTable<T extends { id?: string | number }>({
  queryKey,
  path,
  columns,
  searchable = true,
  searchPlaceholder = 'Search…',
  filters = [],
  params = {},
  onRowClick,
  onTotal,
  onQuery,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  actions,
  title,
  description,
  pageSize = 25,
  refetchInterval,
}: DataTableProps<T>) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  // Debounce so typing does not fire a request per keystroke. This must be an
  // effect, not a memo: it schedules a timer and sets state, and its cleanup has
  // to actually run between keystrokes.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const query = useMemo(
    () => ({
      page,
      page_size: pageSize,
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      ...filterValues,
      ...params,
    }),
    [page, pageSize, debouncedSearch, filterValues, params],
  );

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: [queryKey, query],
    queryFn: () => api.get<Paginated<T>>(path, query),
    placeholderData: keepPreviousData,
    refetchInterval,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  // Reported after render, not during: calling a parent's setState inside a
  // render is what turns a count into an infinite loop.
  useEffect(() => {
    onTotal?.(total);
  }, [onTotal, total]);

  useEffect(() => {
    const { page: _page, page_size: _size, ...rest } = query;
    onQuery?.(rest);
  }, [onQuery, query]);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const toolbar =
    searchable || filters.length > 0 || actions ? (
      <div className="flex flex-wrap items-center gap-2">
        {searchable && (
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-56"
            type="search"
          />
        )}
        {filters.map((filter) => (
          <Select
            key={filter.key}
            value={filterValues[filter.key] ?? ''}
            onChange={(e) => {
              setFilterValues((prev) => ({ ...prev, [filter.key]: e.target.value }));
              setPage(1);
            }}
            className="w-auto"
            aria-label={filter.label}
          >
            <option value="">{filter.label}: all</option>
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        ))}
        {actions}
      </div>
    ) : null;

  return (
    <Card title={title} description={description} actions={toolbar} padded={false}>
      {isPending ? (
        <Loading />
      ) : error ? (
        <div className="p-4">
          <ErrorState error={error} retry={() => refetch()} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      ) : (
        <div className="glee-table-wrap">
          <table className="glee-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cx(column.numeric && 'num')}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={String(row.id ?? index)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cx(onRowClick && 'is-clickable')}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cx(column.numeric && 'num', column.className)}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between border-t border-line px-4 py-2 text-xs text-ink-500 dark:text-ink-400">
          <span className="tabular-nums">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{' '}
            {total.toLocaleString()}
            {isFetching && ' · updating…'}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
